import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProductCard } from '../components/custom/ProductCard';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Heart, Share2, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { trackWishlistShare } from '../utils/tracking';

function WishlistPage() {
  const [wishlist, setWishlist] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    setLoading(true);
    try {
      const savedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      setWishlist(savedWishlist);

      if (savedWishlist.length > 0) {
        const productPromises = savedWishlist.map(id =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`).then(r => r.json())
        );
        const productsData = await Promise.all(productPromises);
        setProducts(productsData.filter(p => p && p.id));
      }
    } catch (error) {
      console.error('Failed to load wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = (productId) => {
    const updated = wishlist.filter(id => id !== productId);
    localStorage.setItem('wishlist', JSON.stringify(updated));
    setWishlist(updated);
    setProducts(products.filter(p => p.id !== productId));
    toast.success('Removed from wishlist');
  };

  const addAllToCart = () => {
    products.forEach(product => {
      addItem(product, 1);
    });
    toast.success(`Added ${products.length} items to cart`);
    navigate('/cart');
  };

  const shareWishlist = async () => {
    try {
      const shareUrl = `${window.location.origin}/wishlist/shared?items=${wishlist.join(',')}`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'My Mothering Wishlist',
          text: 'Check out my wishlist on Mothering!',
          url: shareUrl
        });
        trackWishlistShare();
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Wishlist link copied to clipboard');
        trackWishlistShare();
      }
    } catch (error) {
      console.error('Failed to share:', error);
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-8 py-6 px-4">
      <Helmet>
        <title>My Wishlist | Mothering</title>
        <meta name="description" content="View your saved products" />
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Heart className="w-6 h-6 md:w-7 md:h-7 text-pink-500 fill-pink-500" />
                My Wishlist
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {products.length} {products.length === 1 ? 'item' : 'items'}
              </p>
            </div>
            
            {products.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={shareWishlist}
                  className="gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                <Button
                  size="sm"
                  onClick={addAllToCart}
                  className="gap-2 bg-cyan-600 hover:bg-cyan-700"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span className="hidden sm:inline">Add All to Cart</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-20 h-20 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Your wishlist is empty</h3>
            <p className="text-muted-foreground mb-6">Save your favorite products here</p>
            <Button onClick={() => navigate('/products')} className="bg-cyan-600 hover:bg-cyan-700">
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {products.map((product) => (
              <div key={product.id} className="relative">
                <ProductCard product={product} />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full shadow-lg"
                  onClick={() => removeFromWishlist(product.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WishlistPage;
